import { useEffect, useMemo, useState, useCallback, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { THEME_STORAGE_KEY } from '@guanwei/shared';
import type { ThemeMode } from '@guanwei/shared';

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const VALID_MODES: ThemeMode[] = ['light', 'dark', 'system'];

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (raw && (VALID_MODES as string[]).includes(raw)) return raw as ThemeMode;
  return 'system';
}

function applyDataTheme(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  // 同步 color-scheme 让原生表单/滚动条等跟随
  document.documentElement.style.colorScheme = resolved;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** 默认模式，仅在用户尚未选择时生效 */
  defaultMode?: ThemeMode;
}

/**
 * 主题提供者
 *
 * 三态：light / dark / system
 * 持久化：localStorage（key 来自 shared/constants）
 * 首屏防闪烁：依赖 index.html 中的内联脚本提前写入 data-theme
 */
export function ThemeProvider({ children, defaultMode = 'system' }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = readStoredMode();
    return stored ?? defaultMode;
  });

  const [systemDark, setSystemDark] = useState<boolean>(() => getSystemPrefersDark());

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  // 监听系统主题变化
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // 应用到 DOM
  useEffect(() => {
    applyDataTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
